import { useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, ChevronRight, Package, Pencil, Plus, QrCode, Trash2, X } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** Pallet list with inline rename + delete (delete clears the pallet from its panels). */
export function PalletManager({ projectId, pallets, canManage, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  const create = () =>
    run(async () => {
      await api.post(`/projects/${projectId}/pallets`, { code: newCode.trim() });
      toast.success("Pallet created");
      setNewCode("");
      setCreating(false);
    });

  const rename = (pallet) =>
    run(async () => {
      await api.patch(`/projects/${projectId}/pallets/${pallet.id}`, { code: draft.trim() });
      toast.success("Pallet renamed");
      setEditing(null);
    });

  const remove = (pallet) => {
    const label = pallet.panel_count
      ? `Delete pallet ${pallet.code}? ${pallet.panel_count} panel(s) will lose their pallet info.`
      : `Delete pallet ${pallet.code}?`;
    if (!window.confirm(label)) return;
    run(async () => {
      const { data } = await api.delete(`/projects/${projectId}/pallets/${pallet.id}`);
      toast.success(
        `Pallet deleted · ${data.panels_cleared} panel${data.panels_cleared === 1 ? "" : "s"} cleared`
      );
    });
  };

  const unassign = (pallet, panel) =>
    run(async () => {
      await api.put(`/projects/${projectId}/pallets/assign`, {
        pallet_id: null,
        panel_uids: [panel.panel_uid],
      });
      toast.success(`${panel.panel_name} removed from ${pallet.code}`);
    });

  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white" data-testid="pallet-manager">
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5">
        <h3 className="mono flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-600">
          <Package size={14} /> Pallets ({pallets.length})
        </h3>
        <div className="flex items-center gap-2">
        {canManage ? (
          creating ? (            <div className="flex items-center gap-1.5">
              <input
                data-testid="pallet-new-code-input"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                placeholder="Pallet code"
                className="mono h-8 w-36 rounded-md border border-slate-200 bg-[#EEF2F8] px-2 text-xs"
              />
              <Button
                data-testid="pallet-create-confirm-button"
                onClick={create}
                disabled={busy || !newCode.trim()}
                className="h-8 rounded-md bg-cyan-600 px-3 text-[10px] uppercase text-white hover:bg-cyan-500"
              >
                Add
              </Button>
              <button
                data-testid="pallet-create-cancel-button"
                onClick={() => setCreating(false)}
                className="grid h-8 w-8 place-items-center rounded-md text-slate-500 hover:bg-slate-100"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              data-testid="pallet-create-button"
              onClick={() => setCreating(true)}
              className="mono flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider text-cyan-700 hover:border-cyan-400"
            >
              <Plus size={12} /> New pallet
            </button>
          )
        ) : null}
        {canManage && pallets.length ? (
          <a
            data-testid="pallet-labels-all-pdf"
            href={`${API}/projects/${projectId}/exports/pallet-labels.pdf`}
            target="_blank"
            rel="noreferrer"
            className="mono flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-700 hover:border-cyan-400 hover:text-cyan-700"
          >
            <QrCode size={12} /> All labels
          </a>
        ) : null}
        </div>
      </div>

      {pallets.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500">No pallets yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {pallets.map((pallet) => (
            <li
              key={pallet.id}
              data-testid={`pallet-row-${pallet.code}`}
              className="px-5 py-2.5"
            >
              <div className="flex items-center justify-between gap-3">
              {editing === pallet.id ? (
                <div className="flex items-center gap-1.5">
                  <input
                    data-testid="pallet-rename-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="mono h-8 w-36 rounded-md border border-slate-200 bg-[#EEF2F8] px-2 text-xs"
                  />
                  <button
                    data-testid="pallet-rename-confirm-button"
                    onClick={() => rename(pallet)}
                    disabled={busy || !draft.trim()}
                    className="grid h-8 w-8 place-items-center rounded-md text-emerald-600 hover:bg-emerald-50"
                  >
                    <Check size={15} />
                  </button>
                  <button
                    data-testid="pallet-rename-cancel-button"
                    onClick={() => setEditing(null)}
                    className="grid h-8 w-8 place-items-center rounded-md text-slate-500 hover:bg-slate-100"
                  >
                    <X size={15} />
                  </button>
                </div>
              ) : (
                <button
                  data-testid={`pallet-toggle-${pallet.code}`}
                  onClick={() => setExpanded(expanded === pallet.id ? null : pallet.id)}
                  className="flex flex-1 items-center gap-2 text-left"
                >
                  {expanded === pallet.id ? (
                    <ChevronDown size={14} className="shrink-0 text-slate-500" />
                  ) : (
                    <ChevronRight size={14} className="shrink-0 text-slate-500" />
                  )}
                  <span>
                    <span className="mono block text-sm font-medium text-slate-800">
                      {pallet.code}
                    </span>
                    <span className="block text-[11px] text-slate-500">
                      {pallet.panel_count} panel(s)
                      {pallet.shipment_codes?.length
                        ? ` · ${pallet.shipment_codes.join(", ")}`
                        : ""}
                      {pallet.created_by ? ` · ${pallet.created_by}` : ""}
                    </span>
                  </span>
                </button>
              )}

              {canManage && editing !== pallet.id ? (
                <div className="flex items-center gap-1">
                  <a
                    data-testid={`pallet-label-${pallet.code}`}
                    href={`${API}/projects/${projectId}/exports/pallet-labels.pdf?pallet_id=${pallet.id}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Print QR label (A5)"
                    aria-label={`Print QR label for pallet ${pallet.code}`}
                    className="grid h-8 w-8 place-items-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-cyan-700"
                  >
                    <QrCode size={14} />
                  </a>
                  <button
                    data-testid={`pallet-edit-${pallet.code}`}
                    title="Rename pallet"
                    aria-label={`Rename pallet ${pallet.code}`}
                    onClick={() => {
                      setEditing(pallet.id);
                      setDraft(pallet.code);
                    }}
                    className="grid h-8 w-8 place-items-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-cyan-700"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    data-testid={`pallet-delete-${pallet.code}`}
                    title="Delete pallet"
                    aria-label={`Delete pallet ${pallet.code}`}
                    onClick={() => remove(pallet)}
                    disabled={busy}
                    className="grid h-8 w-8 place-items-center rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ) : null}
              </div>

              {expanded === pallet.id ? (
                <div
                  data-testid={`pallet-panels-${pallet.code}`}
                  className="okn-scroll mt-2.5 max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-[#F7F9FC] p-2.5"
                >
                  {pallet.panels?.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {pallet.panels.map((panel) => (
                        <span
                          key={panel.panel_uid}
                          className="mono flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-700"
                        >
                          {panel.panel_name}
                          {canManage ? (
                            <button
                              data-testid={`pallet-panel-remove-${panel.panel_name}`}
                              title={`Remove ${panel.panel_name} from ${pallet.code}`}
                              onClick={() => unassign(pallet, panel)}
                              disabled={busy}
                              className="grid h-4 w-4 place-items-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600"
                            >
                              <X size={10} />
                            </button>
                          ) : null}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11.5px] text-slate-500">
                      No panels assigned to this pallet yet.
                    </p>
                  )}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
